import { useEffect, useState } from "react";
import { useApproveExemptedBayHireRequest } from "@features/bay-hire/hooks/useBayHireMutations";
import {
  approveExemptedDialogDefaultValues,
  approveExemptedDialogSchema,
  type ApproveExemptedDialogFormData,
} from "@features/bay-hire/schema/approveExemptedDialogSchema";
import { getApiErrorDetail } from "@features/bay-hire/utils/getApiErrorDetail";
import { zodResolver } from "@hookform/resolvers/zod";
import { type ApproveExemptedRequest } from "@services/bayHireApi";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UseApproveExemptedDialogProps {
  open: boolean;
  requestId: string;
  /** Vendor-requested window - seeds the approved dates silently, same as usePreApproveDialog. */
  dateFrom?: string | null;
  dateTo?: string | null;
  onSuccess: () => void | Promise<void>;
}

interface UseApproveExemptedDialogResult {
  form: ReturnType<typeof useForm<ApproveExemptedDialogFormData>>;
  submitError: string | null;
  isSubmitting: boolean;
  /** True while the "Confirm Bay Hire Approval" step is open. */
  showConfirm: boolean;
  /** Validates the form and opens the confirm step - does NOT submit yet. */
  handleSubmitForm: (data: ApproveExemptedDialogFormData) => void;
  /** Actually submits the direct approval, called from the confirm dialog's own Confirm button. */
  handleConfirmSubmit: () => Promise<void>;
  /** Closes the confirm step without submitting, leaving the form as the user left it. */
  handleCancelConfirm: () => void;
}

/**
 * DFT-0084: EXEMPTED-only direct-approve dialog logic. Deliberately much smaller than
 * usePreApproveDialog - no HOD upload (EXEMPTED's HOD document is already vendor-supplied at
 * creation, DFT-0044 D39/AC9, unchanged by this task), no supporting-document upload, no
 * chargeable/totalAmount handling (EXEMPTED is never chargeable).
 */
export const useApproveExemptedDialog = (
  props: UseApproveExemptedDialogProps,
): UseApproveExemptedDialogResult => {
  const { open, requestId, dateFrom, dateTo, onSuccess } = props;
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<ApproveExemptedRequest | null>(null);

  const approveExemptedMutation = useApproveExemptedBayHireRequest();

  const form = useForm<ApproveExemptedDialogFormData>({
    resolver: zodResolver(approveExemptedDialogSchema),
    defaultValues: approveExemptedDialogDefaultValues,
  });

  // Same seeding pattern as usePreApproveDialog's own open effect - Operations approves the
  // vendor's requested window as-is, no visible date fields (Megaplan 2026-08-05).
  useEffect(() => {
    if (open) {
      form.reset({
        ...approveExemptedDialogDefaultValues,
        dateFromApproved: dateFrom ?? "",
        dateToApproved: dateTo ?? "",
      });
      return;
    }
    setSubmitError(null);
    setShowConfirm(false);
    setPendingRequest(null);
    form.reset(approveExemptedDialogDefaultValues);
  }, [open, form, dateFrom, dateTo]);

  const handleSubmitForm = (data: ApproveExemptedDialogFormData) => {
    setPendingRequest({
      dateFromApproved: data.dateFromApproved,
      dateToApproved: data.dateToApproved,
      preApprovalRemarks: data.preApprovalRemarks || undefined,
    });
    setSubmitError(null);
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingRequest) return;
    setSubmitError(null);
    try {
      await approveExemptedMutation.mutateAsync({ id: requestId, request: pendingRequest });
      setShowConfirm(false);
      setPendingRequest(null);
      await onSuccess();
    } catch (error) {
      console.error("Direct-approve (exempted) submission failed:", error);
      setShowConfirm(false);
      // DFT-0084 (mirrors usePreApproveDialog's own AD-5/AC16 note): this is a signal-based Ops
      // decision - a domain-side rejection may never reach here as an HTTP error at all, so the
      // fallback below is what most users actually see for this flow.
      setSubmitError(getApiErrorDetail(error, t("bay-hire.approve-exempted.submit-error")));
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingRequest(null);
  };

  return {
    form,
    submitError,
    isSubmitting: approveExemptedMutation.isPending,
    showConfirm,
    handleSubmitForm,
    handleConfirmSubmit,
    handleCancelConfirm,
  };
};
